import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Cake, PartyPopper, Sparkles } from 'lucide-react';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const BirthdayCelebration = ({ onDismiss }) => {
  const [birthdayData, setBirthdayData] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    checkBirthday();
  }, []);

  const checkBirthday = async () => {
    try {
      const response = await axios.get(`${API}/user/birthday-check`, { withCredentials: true });
      if (response.data.is_birthday) {
        setBirthdayData(response.data);
        // Check if already shown today
        const today = new Date().toISOString().split('T')[0];
        const lastShown = localStorage.getItem('birthday_celebration_shown');
        if (lastShown !== today) {
          setShow(true);
          localStorage.setItem('birthday_celebration_shown', today);
        }
      }
    } catch (error) {
      console.error('Error checking birthday:', error);
    }
  };

  const handleClose = () => {
    setShow(false);
    if (onDismiss) onDismiss();
  };

  if (!show || !birthdayData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl border-2 border-amber-200 relative">
        {/* Confetti decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-4 left-4 animate-bounce" style={{ animationDelay: '0.1s' }}>🎈</div>
          <div className="absolute top-8 right-8 animate-bounce" style={{ animationDelay: '0.2s' }}>🎊</div>
          <div className="absolute bottom-20 left-8 animate-bounce" style={{ animationDelay: '0.3s' }}>🎁</div>
          <div className="absolute bottom-24 right-4 animate-bounce" style={{ animationDelay: '0.15s' }}>✨</div>
        </div>

        {/* Content */}
        <div className="relative p-8 text-center">
          {/* Main icon */}
          <div className="relative inline-block mb-6">
            <div className="text-7xl animate-pulse">🎂</div>
            <div className="absolute -top-2 -right-2">
              <PartyPopper className="h-8 w-8 text-amber-500 animate-bounce" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-amber-800 mb-2">
            Joyeux anniversaire {birthdayData.first_name} ! 🎉
          </h2>

          {/* Age badge */}
          {birthdayData.age && (
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-400 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
              <Sparkles className="h-4 w-4" />
              {birthdayData.age} ans aujourd'hui !
            </div>
          )}

          {/* Message */}
          <p className="text-amber-700 text-lg leading-relaxed mb-6">
            Aujourd'hui, c'est <strong>ta</strong> journée ! 
            <br />
            Ton coach financier te souhaite une merveilleuse année. 
            <br />
            Continue de gérer ton budget comme un pro ! 💪
          </p>

          {/* Motivational quote */}
          <div className="bg-white/60 rounded-xl p-4 mb-6 border border-amber-200">
            <p className="text-sm text-amber-600 italic">
              "Une nouvelle année commence pour toi. 
              <br />
              Que tous tes projets se réalisent ! 🌟"
            </p>
          </div>

          {/* Close button */}
          <Button
            onClick={handleClose}
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold"
            size="lg"
            data-testid="birthday-close-btn"
          >
            <Cake className="h-5 w-5 mr-2" />
            Merci ! C'est parti pour cette nouvelle année
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BirthdayCelebration;
